import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react'

type Variante = 'principal' | 'secondaire' | 'discret' | 'danger'

const VARIANTES: Record<Variante, string> = {
  principal: 'bg-sky-600 text-white hover:bg-sky-700 disabled:bg-sky-300',
  secondaire: 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 disabled:text-slate-400',
  discret: 'text-slate-600 hover:bg-slate-100 disabled:text-slate-300',
  danger: 'bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300',
}

export function Bouton({
  variante = 'principal',
  className = '',
  children,
  ...reste
}: ButtonHTMLAttributes<HTMLButtonElement> & { variante?: Variante }) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed ${VARIANTES[variante]} ${className}`}
      {...reste}
    >
      {children}
    </button>
  )
}

/**
 * Champ de formulaire avec libellé, erreur et description.
 *
 * L'erreur est liée au champ par `aria-describedby` : sans cela, un lecteur
 * d'écran annonce « champ invalide » sans jamais dire pourquoi.
 */
export function Champ({
  libelle,
  erreur,
  aide,
  obligatoire,
  children,
  id,
}: {
  libelle: string
  erreur?: string
  aide?: string
  obligatoire?: boolean
  children: ReactNode
  id: string
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {libelle}
        {obligatoire ? <span className="ml-0.5 text-red-600" aria-hidden="true">*</span> : null}
      </label>
      {children}
      {aide && !erreur ? (
        <p id={`${id}-aide`} className="text-xs text-slate-500">
          {aide}
        </p>
      ) : null}
      {erreur ? (
        <p id={`${id}-erreur`} className="text-xs font-medium text-red-700" role="alert">
          {erreur}
        </p>
      ) : null}
    </div>
  )
}

const CLASSE_SAISIE =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 disabled:bg-slate-100'

/**
 * `forwardRef` est obligatoire, pas cosmetique.
 *
 * React Hook Form passe une `ref` a chaque champ enregistre : c'est par elle
 * qu'il donne le focus au premier champ en erreur a la soumission. Sans la
 * transmettre, React avertit en console et le focus ne part nulle part —
 * l'utilisateur voit un message d'erreur sans savoir ou corriger.
 */
export const Saisie = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Saisie({ className = '', ...reste }, ref) {
    return <input ref={ref} className={`${CLASSE_SAISIE} ${className}`} {...reste} />
  },
)

export const Liste = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Liste({ className = '', children, ...reste }, ref) {
    return (
      <select ref={ref} className={`${CLASSE_SAISIE} ${className}`} {...reste}>
        {children}
      </select>
    )
  },
)

/** Pastille de couleur — jamais seule porteuse d'information (daltonisme). */
export function Pastille({ couleur, titre }: { couleur: string; titre?: string }) {
  return (
    <span
      className="inline-block h-3 w-3 shrink-0 rounded-full ring-1 ring-black/10"
      style={{ backgroundColor: couleur }}
      title={titre}
      aria-hidden="true"
    />
  )
}

export function Etiquette({
  ton = 'neutre',
  children,
}: {
  ton?: 'neutre' | 'succes' | 'attention' | 'danger' | 'info'
  children: ReactNode
}) {
  const styles = {
    neutre: 'bg-slate-100 text-slate-700',
    succes: 'bg-emerald-100 text-emerald-800',
    attention: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800',
    info: 'bg-sky-100 text-sky-800',
  } as const
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[ton]}`}>
      {children}
    </span>
  )
}
